import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ReplyChip } from './ReplyChip';
import { DockNote } from './DockNote';
import { ThinkingDots } from './ThinkingDots';
import { useChatConversation } from '../hooks/useChatConversation';
import { DOCK_MENU, dockScript } from '../data/dockScript';
import { dockBox } from '../utils/dock';
import { questFilter, runPixelAction } from '../utils/pixelActions';
import type { ChatNode, ChatOption, ChatPhase } from '../types/chat';
import type { Emotion } from '../types/emotion';
interface DockChatProps {
  reduce: boolean;
  /** Pixel has touched down in the corner (false the moment he's about to leave) */
  landed: boolean;
  onPhase: (phase: ChatPhase, emotion: Emotion) => void;
  onPulse: () => void;
}

/** Extra reach (px) around Pixel that still counts as hovering him */
const NEAR_PAD = 40;
/** Notes on screen at once */
const MAX_STACK = 4;
/** After the last note of a topic, the notes stay this long before popping away (ms) */
const NOTES_LINGER = 6000;
/** Height reserved under the notes for the thinking bubble (px) */
const THINK_SLOT = 28;

/**
 * Corner chat: hover (or come near) the docked Pixel and three blue quick-picks pop up on his left.
 * Moving away hides them right away. Picking one starts a short burst of grey notes above him.
 * Everything pops away the instant he's about to leave the corner.
 */
export function DockChat({
  reduce,
  landed,
  onPhase,
  onPulse
}: DockChatProps) {
  const [near, setNear] = useState(false);
  const [notesHidden, setNotesHidden] = useState(true);
  /** A topic is playing — quick-picks stay hidden until Pixel has sent his last note */
  const [talking, setTalking] = useState(false);
  const [vw, setVw] = useState(() => typeof window === 'undefined' ? 1280 : window.innerWidth);
  const chipsRef = useRef<HTMLDivElement>(null);
  const tapRef = useRef<HTMLButtonElement>(null);

  /** How often each node has been heard — one-offs disappear, utility nodes switch to their `again` lines */
  const visitsRef = useRef(new Map<string, number>());
  const [visitTick, setVisitTick] = useState(0);
  /** The node Pixel is on — its options are the follow-up picks */
  const [current, setCurrent] = useState<string | null>(null);
  const visibleRef = useRef(false);
  visibleRef.current = landed && !notesHidden;
  const linesFor = useCallback((node: ChatNode) => (visitsRef.current.get(node.id) ?? 0) > 1 && node.again ? node.again : node.lines, []);
  const chat = useChatConversation({
    reduce,
    visibleRef,
    onPhase,
    onPulse,
    script: dockScript,
    linesFor
  });
  const {
    play,
    pause
  } = chat;

  /** Hide options leading to one-off topics already heard, so nothing ever repeats */
  const available = useCallback((options: ChatOption[]) => questFilter(options).filter(o => {
    const target = dockScript.find(n => n.id === o.next);
    // Quest invitations stay on offer until the quest is played
    return !!target && (!!o.quest || !!target.again || !visitsRef.current.has(o.next));
  }),
  // visitTick: re-filter after every pick
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [visitTick]);
  const menuOptions = useMemo(() => available(dockScript.find(n => n.id === DOCK_MENU)?.options ?? []), [available]);
  const followUps = useMemo(() => {
    const node = current ? dockScript.find(n => n.id === current) : null;
    return node ? available(node.options) : [];
  }, [current, available]);
  const {
    size,
    margin
  } = dockBox(vw);
  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Hover zone: Pixel's box (+ padding), plus the chips themselves while they're out
  useEffect(() => {
    if (!landed) {
      setNear(false);
      return;
    }
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      const w = window.innerWidth;
      const h = window.innerHeight;
      const left = w - margin - size - NEAR_PAD;
      const top = h - margin - size - NEAR_PAD;
      let inside = e.clientX >= left && e.clientY >= top;
      const chips = chipsRef.current?.getBoundingClientRect();
      if (!inside && chips && chips.width > 0) {
        inside = e.clientX >= chips.left - 12 && e.clientX <= chips.right + 12 && e.clientY >= chips.top - 12 && e.clientY <= chips.bottom + 12;
      }
      setNear(n => n === inside ? n : inside);
    };
    const onLeave = () => setNear(false);
    // Touch: tapping Pixel is handled by the real tap target below (it toggles the quick-picks);
    // tapping anywhere else (except the chips) closes them
    const onTap = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      const inRect = (r: DOMRect | undefined) => !!r && r.width > 0 && e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      if (inRect(chipsRef.current?.getBoundingClientRect()) || inRect(tapRef.current?.getBoundingClientRect())) return;
      setNear(false);
    };
    window.addEventListener('pointermove', onMove, {
      passive: true
    });
    window.addEventListener('pointerup', onTap, true);
    document.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onTap, true);
      document.removeEventListener('pointerleave', onLeave);
    };
  }, [landed, margin, size]);

  // About to fly out: stop talking, everything pops away immediately
  useEffect(() => {
    if (landed) return;
    pause();
    setNotesHidden(true);
    setTalking(false);
  }, [landed, pause]);

  // Last note sent → the follow-up picks may come out
  useEffect(() => {
    if (talking && chat.ready) setTalking(false);
  }, [talking, chat.ready]);

  // Answer finished and the visitor wandered off: notes linger, then pop away and Pixel relaxes.
  // Hovering him again brings back the main menu (minus what he's already told you).
  useEffect(() => {
    if (!chat.ready || notesHidden || near) return;
    const id = window.setTimeout(() => {
      setNotesHidden(true);
      setCurrent(null);
      onPhase('idle', 'neutral');
    }, NOTES_LINGER);
    return () => window.clearTimeout(id);
  }, [chat.ready, notesHidden, near, onPhase]);
  const choose = (option: ChatOption) => {
    const visits = visitsRef.current;
    visits.set(option.next, (visits.get(option.next) ?? 0) + 1);
    setVisitTick(v => v + 1);
    setCurrent(option.next);
    setNotesHidden(false);
    setTalking(true);
    chat.markInteracted();
    onPhase('reacting', option.reaction);
    // Confirmed actions happen right away (the email draft must open from the click itself)
    if (option.action) runPixelAction(option.action);
    play(option.next, reduce ? 0 : 250);
  };
  const notesOn = landed && !notesHidden;
  const notes = notesOn ? chat.messages.filter(m => m.from === 'bot' && m.burst === chat.burst).slice(-MAX_STACK) : [];
  // After Pixel has answered (notes still up), his follow-up picks come out on their own
  const answered = chat.ready && notesOn && notes.length > 0 && followUps.length > 0;
  const picks = answered ? followUps : menuOptions;
  const chipsOn = landed && !talking && (near || answered) && picks.length > 0;
  // Notes column: a little left of Pixel's head, its bottom just above the thinking slot
  const notesRight = margin + size * 0.98 + 6;
  const notesBottom = margin + size * 0.5 + THINK_SLOT;
  return (
    // Above every section (reel, work cards) and above Pixel's own layer, so notes and dots are never hidden
    <div className="pointer-events-none fixed inset-0 z-[66]" aria-live="off" data-id="element-57" data-mp="8b75g8bkm2ad">
      {/* Touch screens: a real (invisible) tap target over the docked Pixel. Working out taps from raw
          coordinates missed too easily on phones (address bar, small hit box), so the menu never opened.
          Hover devices open the menu by coming near instead, so it's hidden there. */}
      {landed && <button ref={tapRef} type="button" aria-label="Chat with Pixel" aria-expanded={chipsOn} onClick={() => setNear(n => !n)} className="pointer-events-auto absolute touch-manipulation rounded-full [-webkit-tap-highlight-color:transparent] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black [@media(hover:hover)]:hidden" style={{
        right: margin - 12,
        bottom: margin - 12,
        width: size + 24,
        height: size + 24
      }} data-id="element-58" data-mp="wex0by1is7gk" />}
      {/* Grey notes — up and to the left of Pixel's head, with breathing room */}
      <div role="log" aria-live="polite" aria-label="Pixel says"
      // Notes sit just above a fixed thinking slot, so the stack never jumps when the dots come and go.
      // Bottom-anchored: a note leaving the top never shifts the ones below it; a new note at the
      // bottom glides the others up (layout animation). No CSS scaling here — it breaks that.
      className="absolute flex w-max flex-col items-end gap-[5px]" style={{
        right: notesRight,
        bottom: notesBottom
      }} data-id="element-59" data-mp="3lxfiqvlygvjr">
        <AnimatePresence initial={false} data-id="element-60" data-mp="17mmp82vbehw">
          {notes.map((m, i) => <DockNote key={m.id} text={m.text} tail={i === notes.length - 1} reduce={reduce} data-id="element-61" data-mp="3sdxhimhabj0s" data-mpi={i} />)}
        </AnimatePresence>
      </div>

      {/* Thinking bubble — its own fixed spot right under the notes, toward Pixel's head */}
      <div className="absolute flex origin-bottom-right scale-[0.99] justify-end" style={{
        right: notesRight,
        bottom: margin + size * 0.5
      }} data-id="element-62" data-mp="3jc7r9jz52q4l">
        <AnimatePresence data-id="element-63" data-mp="27xazi2bklmtj">
          {notesOn && chat.thinking && <ThinkingDots key="think" reduce={reduce} tail="bottom-right" data-id="element-64" data-mp="27wj5gvxmfx5d" />}
        </AnimatePresence>
      </div>

      {/* Blue quick-picks — on Pixel's left, close to him */}
      <div ref={chipsRef} role="group" aria-label="Ask Pixel"
      // Compact: chips wrap into rows, tight gaps, right-aligned next to Pixel
      // Opening picks: one vertical column beside Pixel.
      // Follow-ups: one horizontal row directly under his grey notes (the thinking slot is free by then).
      className={`absolute flex scale-[0.92] ${answered ? 'origin-top-right flex-row flex-wrap items-start justify-end gap-[5px]' : 'origin-bottom-right flex-col items-end gap-[5px]'}`}
      // Opening picks (nothing said yet) sit higher and tucked in closer to Pixel;
      // follow-up picks stay low, since his grey notes take the upper spot (wrapping onto a
      // second row rather than running off the screen's left edge)
      style={answered ? {
        right: notesRight,
        top: `calc(100% - ${notesBottom - 14}px)`,
        width: 'max-content',
        maxWidth: Math.min(460, vw - notesRight - 16)
      } : {
        right: margin + size * 0.93,
        bottom: margin + size * 0.42
      }} data-id="element-65" data-mp="e72s8t1ecjvd">
        <AnimatePresence data-id="element-66" data-mp="2orafs73p0020">
          {chipsOn && picks.map((option, i) => <ReplyChip key={`${answered ? current : 'menu'}-${option.label}`} option={option} index={i} count={picks.length} picked={false} disabled={false} reduce={reduce} onChoose={choose}
          // Opening column: the bottom chip curls a tail toward Pixel
          tail={!answered && i === picks.length - 1} data-id="element-67" data-mp="2tq4zrcdebunp" data-mpi={i} />)}
        </AnimatePresence>
      </div>
    </div>
  );
}